import { useState, useEffect, useContext } from "react";
import { VictoryBar, VictoryChart, VictoryAxis, VictoryStack, VictoryTooltip } from "victory";
import { addDays, format } from "date-fns";
import ManagerNav from "./managernav";
import { AuthContext } from "../AuthContext";
import { ArrowLongRightIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import AdminInteractions from "./admininteractionsnonav";


export default function TaskGraph() {
  const yesterday = addDays(new Date(), -1).toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(yesterday);
  const [toDate, setToDate] = useState(yesterday);
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");

  const navigate = useNavigate();
  const user = useContext(AuthContext);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await fetch("https://api.insightos.co.uk/api/taskgraph", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',

          body: JSON.stringify({ fromDate, toDate }),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        const tasks = Array.isArray(result.data) ? result.data : [];
        const fetchedReports = result.reports || result.data?.reports || [];

        console.log("Fetched tasks:", tasks);
        console.log("Fetched reports:", fetchedReports);

        setReports(fetchedReports);
        setSelectedReport(fetchedReports.length > 0 ? fetchedReports[0].Content || "No report available" : "No report available");

        const filteredTasks = tasks.filter((task) => task.Task !== "Break");

        const graphDataMap = filteredTasks.reduce((acc, task) => {
          const name = task.Name || "Unknown";
          if (!acc[name]) acc[name] = { Name: name, Completed: 0, Incomplete: 0, IncompleteTasks: [] };
          if (task.Completed === 1) acc[name].Completed += 1;
          else {
            acc[name].Incomplete += 1;
            acc[name].IncompleteTasks.push(task.Task || "Unnamed Task");
          }
          return acc;
        }, {});

        setGraphData(Object.values(graphDataMap));
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
      setLoading(false);
    };

    getData();
  }, [fromDate, toDate]);

  // Ensure selectedReport is always a string
  const formattedReport = (selectedReport || "").replace(/\s*\*\*(.*?)\*\*/g, "\n\n**$1**");
  return (
    <>
      <ManagerNav />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 px-3 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 flex items-center justify-center">
                <span className="text-2xl sm:text-3xl mr-2">📊</span>
                Task Reports & Analytics
              </h1>
              <p className="text-sm sm:text-base text-blue-200">
                Comprehensive task completion reports and performance insights
              </p>
            </div>
          </div>

          {/* Controls Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="space-y-4">
              {/* Date Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fromDate" className="block text-sm font-semibold text-blue-200 mb-2">
                    📅 From Date
                  </label>
                  <input
                    type="date"
                    id="fromDate"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-xl px-4 py-3 text-white font-medium placeholder-blue-200 hover:border-cyan-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/30 transition-all duration-200 touch-manipulation"
                    data-component="reports-filters"
                    data-feature="set_from_date"
                  />
                </div>
                <div>
                  <label htmlFor="toDate" className="block text-sm font-semibold text-blue-200 mb-2">
                    📅 To Date
                  </label>
                  <input
                    type="date"
                    id="toDate"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-xl px-4 py-3 text-white font-medium placeholder-blue-200 hover:border-cyan-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/30 transition-all duration-200 touch-manipulation"
                    data-component="reports-filters"
                    data-feature="set_to_date"
                  />
                </div>
              </div>
              
              {/* Next Button */}
              <button 
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all duration-200 active:scale-95 touch-manipulation animate-pulse hover:animate-none flex items-center justify-center text-base sm:text-lg"
                onClick={() => navigate("/team-tasks")}
                data-component="reports-actions"
                data-feature="continue_to_team_tasks"
              > 
                <span className="mr-2">Continue to Team Tasks</span>
                <ArrowLongRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Graph Section */}
            <div className="lg:col-span-1 bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-cyan-300 mb-2 flex items-center">
                  <span className="text-xl mr-2">📈</span>
                  Task Completion
                </h2>
                <p className="text-sm text-blue-200">Performance overview</p>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
                    <p className="text-cyan-300 font-medium">Loading analytics...</p>
                  </div>
                </div>
              ) : graphData.length === 0 ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <div className="text-center">
                    <span className="text-4xl sm:text-5xl mb-4 block">📊</span>
                    <p className="text-cyan-300 text-lg font-medium mb-2">No Data Available</p>
                    <p className="text-blue-200 text-sm">Try adjusting your date range</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white/5 rounded-xl p-3 sm:p-4 overflow-x-auto">
                  <VictoryChart 
                    domainPadding={20}
                    width={300}
                    height={300}
                    padding={{ left: 60, top: 20, right: 20, bottom: 60 }}
                  >
                    <VictoryAxis 
                      tickFormat={graphData.map((d) => d.Name.substring(0, 8) + (d.Name.length > 8 ? '...' : ''))} 
                      style={{ 
                        tickLabels: { fill: "#67E8F9", fontSize: 10, angle: -45 },
                        axis: { stroke: "#67E8F9" },
                        grid: { stroke: "transparent" }
                      }} 
                    />
                    <VictoryAxis 
                      dependentAxis 
                      style={{ 
                        tickLabels: { fill: "#67E8F9", fontSize: 10 },
                        axis: { stroke: "#67E8F9" },
                        grid: { stroke: "#374151", strokeOpacity: 0.3 }
                      }} 
                    />
                    <VictoryStack>
                      <VictoryBar
                        data={graphData}
                        x="Name"
                        y="Completed"
                        style={{ data: { fill: "#10B981" } }}
                        labels={({ datum }) => `✅ ${datum.Completed}`}
                        labelComponent={<VictoryTooltip style={{ fill: "white", fontSize: 10 }} />}
                      />
                      <VictoryBar
                        data={graphData}
                        x="Name"
                        y="Incomplete"
                        style={{ data: { fill: "#EF4444" } }}
                        labels={({ datum }) => `⏳ ${datum.Incomplete}`}
                        labelComponent={<VictoryTooltip style={{ fill: "white", fontSize: 10 }} />}
                      />
                    </VictoryStack>
                  </VictoryChart>
                </div>
              )}
            </div>

            {/* Reports Section */}
            <div className="lg:col-span-2 bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-cyan-300 mb-2 flex items-center">
                  <span className="text-xl mr-2">📋</span>
                  Performance Reports
                </h2>
                <p className="text-sm text-blue-200">Detailed analysis and insights</p>
              </div>

              {/* Report Selector */}
              {reports.length > 0 && (
                <div className="mb-6">
                  <label htmlFor="reportSelect" className="block text-sm font-semibold text-blue-200 mb-2">
                    📝 Select Report
                  </label>
                  <select
                    id="reportSelect"
                    value={selectedReport}
                    onChange={(e) => setSelectedReport(e.target.value)}
                    className="w-full bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-xl px-4 py-3 text-white font-medium hover:border-cyan-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/30 transition-all duration-200 touch-manipulation appearance-none text-sm sm:text-base"
                    data-component="reports-selector"
                    data-feature="select_report"
                  >
                    {reports.map((report, index) => (
                      <option key={index} value={report.Content || "No content"} className="bg-gray-800 text-white">
                        {`Report ${index + 1} - ${format(new Date(report.created_at || new Date()), "MMM dd, yyyy")}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

                {/* Report Content */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/10 max-h-80 sm:max-h-96 overflow-y-auto">
                <div className="prose prose-invert max-w-none">
                  {formattedReport.split('\n').map((line, index) => {
                    if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
                      return (
                        <h3 key={index} className="text-base sm:text-lg font-bold text-cyan-300 mt-4 mb-2 first:mt-0">
                          {line.replace(/\*\*/g, '')}
                        </h3>
                      );
                    } else if (line.trim() !== '') {
                      return (
                        <p key={index} className="text-sm sm:text-base text-gray-200 mb-2 leading-relaxed">
                          {line}
                        </p>
                      );
                    } else {
                      return <div key={index} className="h-2"></div>;
                    }
                  })}
                </div>
                <div className= "mt-5 mb-5" >
                <AdminInteractions />
                </div>
              </div>
            </div>
          </div>

          {/* Task Details Section */}
          {graphData.length > 0 && (
            <div className="mt-4 sm:mt-6 bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-cyan-300 mb-4 sm:mb-6 flex items-center">
                <span className="text-xl mr-2">📝</span>
                Incomplete Task Details
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {graphData
                  .filter((person) => person.IncompleteTasks.length > 0)
                  .map((person, index) => (
                    <div
                      key={index}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-cyan-400/50 transition-all duration-200"
                    >
                      <h3 className="text-base sm:text-lg font-bold text-cyan-300 mb-3 flex items-center">
                        <span className="text-lg mr-2">👤</span>
                        {person.Name}
                      </h3>
                      <div className="space-y-2">
                        {person.IncompleteTasks.map((task, taskIndex) => (
                          <div
                            key={taskIndex}
                            className="bg-red-500/20 border border-red-500/30 rounded-lg px-3 py-2 flex items-center"
                          >
                            <span className="text-red-400 mr-2 text-sm">⏳</span>
                            <span className="text-sm text-gray-200 font-medium">{task}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <p className="text-xs text-blue-200">
                          {person.IncompleteTasks.length} incomplete task{person.IncompleteTasks.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
              
              {graphData.filter((person) => person.IncompleteTasks.length > 0).length === 0 && (
                <div className="text-center py-8">
                  <span className="text-4xl sm:text-5xl mb-4 block">🎉</span>
                  <p className="text-lg sm:text-xl font-medium text-green-400 mb-2">Great Work!</p>
                  <p className="text-sm sm:text-base text-blue-200">All tasks have been completed for this period.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
